import { useEffect, useState } from 'react';
import { Box, Button, IconButton, Tooltip, Typography } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';

import { PrimaryTextField } from 'theme/CommonTheme';

import SelectorIdentifierLineChip from './SelectorIdentifierLineChip';
import css from './SelectorIdentifierChipList.module.css';

const DEFAULT_EMPTY_HELPER = 'Add expressions using the builder above.';

/**
 * AND/OR control shown between selector predicates.
 * @param {{ value: 'and'|'or', isReadOnly?: boolean, onChange?: (op: 'and'|'or') => void }} props
 */
export function SelectorJoinControl({ value, isReadOnly = false, onChange }) {
    const op = value === 'or' ? 'or' : 'and';
    if (isReadOnly) {
        return (
            <span
                className={`${css.joinLabel} ${op === 'or' ? css.joinOr : css.joinAnd}`}
                aria-label={op === 'or' ? 'Combined with OR' : 'Combined with AND'}
            >
                {op === 'or' ? 'OR' : 'AND'}
            </span>
        );
    }
    return (
        <span className={css.joinRow} role="group" aria-label="Combine with previous selector">
            <Button
                type="button"
                size="small"
                className={`${css.joinButton} ${op === 'and' ? css.joinAnd : ''}`}
                variant="outlined"
                aria-pressed={op === 'and'}
                onClick={() => onChange?.('and')}
            >
                AND
            </Button>
            <Button
                type="button"
                size="small"
                className={`${css.joinButton} ${op === 'or' ? css.joinOr : ''}`}
                variant="outlined"
                aria-pressed={op === 'or'}
                onClick={() => onChange?.('or')}
            >
                OR
            </Button>
        </span>
    );
}

function visibleEntries(items) {
    return items
        .map((value, index) => ({ value: String(value), index }))
        .filter(({ value }) => value.trim() !== '');
}

/**
 * Chip list in a max-height column flow (fills downward, then wraps to the next column):
 * edit/delete per chip, inline Save/Cancel editor, read-only truncation + tooltip
 * (handled in line chip).
 *
 * @param {object} props
 * @param {string[]} props.items
 * @param {(next: string[], removedIndex?: number) => void} props.onItemsChange
 * @param {boolean} props.isReadOnly
 * @param {string} props.ariaLabelPrefix e.g. "Selectors" or "Identifiers" (UI subsection titles).
 * @param {string} [props.emptyHelperText]
 * @param {string} [props.className]
 * @param {Array<'and'|'or'|null>|undefined} [props.joinOperators] Join before each item. Omit for a plain list.
 * @param {(index: number, op: 'and'|'or') => void} [props.onJoinOperatorChange]
 * @param {boolean} [props.wrapItems] Show each item's full text.
 */
export default function SelectorIdentifierChipList({
    items,
    onItemsChange,
    isReadOnly,
    ariaLabelPrefix,
    emptyHelperText = DEFAULT_EMPTY_HELPER,
    className,
    joinOperators,
    onJoinOperatorChange,
    wrapItems = false
}) {
    const [editingIndex, setEditingIndex] = useState(null);
    const [draft, setDraft] = useState('');

    useEffect(() => {
        if (editingIndex != null && editingIndex >= items.length) {
            setEditingIndex(null);
            setDraft('');
        }
    }, [items, editingIndex]);

    const closeEditor = () => {
        setEditingIndex(null);
        setDraft('');
    };

    const beginEdit = (index) => {
        setEditingIndex(index);
        setDraft(String(items[index] ?? ''));
    };

    const handleSave = () => {
        if (editingIndex == null) return;
        const next = items.slice();
        next[editingIndex] = draft;
        onItemsChange(next);
        closeEditor();
    };

    const handleCancel = () => {
        closeEditor();
    };

    const handleDelete = (index) => {
        // Any delete is a structural change: close inline editor (spec).
        setEditingIndex(null);
        setDraft('');
        onItemsChange(
            items.filter((_, i) => i !== index),
            index
        );
    };

    const handleDraftKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSave();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            handleCancel();
        }
    };

    const entries = visibleEntries(items);
    const showEmptyHelper = !isReadOnly && items.length === 0;
    const showJoins = Array.isArray(joinOperators);

    return (
        <Box className={className}>
            {showEmptyHelper ? (
                <Typography
                    variant="caption"
                    color="textSecondary"
                    component="p"
                    className={css.emptyHelper}
                >
                    {emptyHelperText}
                </Typography>
            ) : null}

            {entries.length > 0 || editingIndex != null ? (
                <Box
                    className={showJoins || wrapItems ? css.stack : css.chipArea}
                    role="list"
                    aria-label={`${ariaLabelPrefix} expressions`}
                >
                    {entries.map(({ value, index }, visiblePosition) =>
                        editingIndex === index ? (
                            <Box
                                key={`edit-${index}`}
                                className={showJoins || wrapItems ? css.stackRow : css.chipItem}
                                role="listitem"
                            >
                                {showJoins && visiblePosition > 0 ? (
                                    <SelectorJoinControl
                                        value={joinOperators[index] === 'or' ? 'or' : 'and'}
                                        isReadOnly={isReadOnly}
                                        onChange={(op) => onJoinOperatorChange?.(index, op)}
                                    />
                                ) : null}
                                <Box className={css.editorRow}>
                                <PrimaryTextField
                                    className={css.editorField}
                                    value={draft}
                                    onChange={(e) => setDraft(e.target.value)}
                                    onKeyDown={handleDraftKeyDown}
                                    size="small"
                                    fullWidth
                                    autoFocus
                                    multiline={wrapItems}
                                    rows={wrapItems ? 3 : 1}
                                    inputProps={{ 'aria-label': `${ariaLabelPrefix} line editor` }}
                                />
                                <span className={css.editorActions}>
                                    <Tooltip title="Save">
                                        <IconButton
                                            size="small"
                                            className={css.editorActionButton}
                                            aria-label={`Save ${ariaLabelPrefix} line`}
                                            onClick={handleSave}
                                            color="primary"
                                        >
                                            <CheckIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Cancel">
                                        <IconButton
                                            size="small"
                                            className={css.editorActionButton}
                                            aria-label={`Cancel editing ${ariaLabelPrefix} line`}
                                            onClick={handleCancel}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </span>
                                </Box>
                            </Box>
                        ) : (
                            <Box
                                key={`chip-${index}`}
                                className={showJoins || wrapItems ? css.stackRow : css.chipItem}
                                component="span"
                                role="listitem"
                            >
                                {showJoins && visiblePosition > 0 ? (
                                    <SelectorJoinControl
                                        value={joinOperators[index] === 'or' ? 'or' : 'and'}
                                        isReadOnly={isReadOnly}
                                        onChange={(op) => onJoinOperatorChange?.(index, op)}
                                    />
                                ) : null}
                                <SelectorIdentifierLineChip
                                    value={value}
                                    isReadOnly={isReadOnly}
                                    wrapText={wrapItems}
                                    onEdit={isReadOnly ? undefined : () => beginEdit(index)}
                                    onDelete={isReadOnly ? undefined : () => handleDelete(index)}
                                    ariaLabels={{
                                        edit: `Edit ${ariaLabelPrefix} line`,
                                        delete: `Delete ${ariaLabelPrefix} line`
                                    }}
                                    lineAriaLabel={`${ariaLabelPrefix}: ${value}`}
                                />
                            </Box>
                        )
                    )}
                </Box>
            ) : null}
        </Box>
    );
}
